import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  Customer,
  CustomerPhone,
  PersonalCouponListItem,
  UpdateCustomerBody,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customerKeys } from './queryKeys';

const CUSTOMER_PATH = '/api/customers/{customerId}';
const PHONE_PATH = '/api/customer-phones/{phoneId}';

export function useCustomer(customerId: string) {
  return useQuery({
    queryKey: customerKeys.detail(customerId),
    queryFn: async () =>
      unwrapResult<Customer>(
        await openapiClient.GET(CUSTOMER_PATH, { params: { path: { customerId } } }),
      ),
    enabled: !!customerId,
  });
}

/** The private coupons a customer holds across promotions (customer record). */
export function useCustomerCoupons(customerId: string) {
  return useQuery({
    // Keyed so useIssuePersonalCoupon can invalidate it by customerId alone.
    queryKey: ['customer-coupons', customerId] as const,
    queryFn: async () => {
      const r = await openapiClient.GET('/api/customers/{customerId}/personal-coupons', {
        params: { path: { customerId }, query: { lang: 'en' } },
      });
      return unwrapResult<PersonalCouponListItem[]>(r);
    },
    enabled: !!customerId,
  });
}

function useInvalidate(customerId: string) {
  const franchiseId = useRestaurantStore((s) => s.selectedFranchiseId) ?? '';
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: customerKeys.detail(customerId) });
    qc.invalidateQueries({ queryKey: customerKeys.all(franchiseId) });
  };
}

export function useUpdateCustomer(customerId: string) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: async (body: UpdateCustomerBody) =>
      unwrapResult<Customer>(
        await openapiClient.PATCH(CUSTOMER_PATH, { params: { path: { customerId } }, body }),
      ),
    onSuccess: invalidate,
  });
}

export function useAddCustomerPhone(customerId: string) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: async (input: { phone: string; label?: string }) =>
      unwrapResult<CustomerPhone>(
        await openapiClient.POST('/api/customers/{customerId}/phones', {
          params: { path: { customerId } },
          body: input,
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useSetPrimaryPhone(customerId: string) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: async (phoneId: string) =>
      unwrapResult<CustomerPhone>(
        await openapiClient.PATCH(PHONE_PATH, {
          params: { path: { phoneId } },
          body: { isPrimary: true },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useRemoveCustomerPhone(customerId: string) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: async (phoneId: string) => {
      unwrapResult(await openapiClient.DELETE(PHONE_PATH, { params: { path: { phoneId } } }));
    },
    onSuccess: invalidate,
  });
}
